import React, { createContext, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import {
  clearNewMessage,
  clearRequestType,
  clearPopTartHistory,
  prevHistory,
  setAshSessionId,
  setPoptartHistory,
} from 'store/MemberCommunication'
import {
  selectPoptartHistory,
  selectRequestTitle,
  selectUnresolvedRequests,
  selectUnresolvedRequestsBusy,
} from 'store/MemberCommunication/selectors'

/**
 * The MemberComms context is used to provide member communication functionality
 * throughout the app, including managing poptart history and request flows.
 */
export const MemberComms = createContext()

/**
 * The MemberCommsProvider is a component that provides member communication
 * functionality to its children components.
 *
 * @param {Object} props - The component props
 * @param {React.ReactNode} props.children - The children components that will
 *   have access to the member comms context
 * @returns {React.ReactNode} The children wrapped in the MemberComms context
 *   provider
 */
export const MemberCommsProvider = ({ children }) => {
  const dispatch = useDispatch()

  // Pull data from Redux using selectors
  const poptartHistory = useSelector(selectPoptartHistory)
  const requestTitle = useSelector(selectRequestTitle)
  const unresolvedRequests = useSelector(selectUnresolvedRequests)
  const unresolvedRequestsBusy = useSelector(selectUnresolvedRequestsBusy)

  // State used to handle the member comms poptart open/close state
  const [isOpen, setIsOpen] = useState(false)
  const [isMaximized, setIsMaximized] = useState(false)

  // Handler to open the member comms poptart
  const openPoptart = (options = {}) => {
    setIsOpen(true)
    setIsMaximized(options.maximized ?? false)
  }
  const closePoptart = () => {
    setIsOpen(false)
    setIsMaximized(false)
  }

  // Handler to toggle maximize state
  const toggleMaximize = maximized => {
    setIsMaximized(maximized)
  }

  // Handler to close the member comms poptart with cleanup
  const handleClose = () => {
    dispatch(clearNewMessage())
    dispatch(clearRequestType())
    dispatch(clearPopTartHistory())
    // An abandoned compose flow produced no ticket, so the Ash conversation that
    // opened it has nothing to do with whatever the member files next. Every
    // cancel path funnels through here via `MemberCommsPoptart.clearData`, and
    // `Inbox/Home` only re-reads the URL on another deep-link arrival, so
    // without this the id would outlive its flow for the rest of the session and
    // stamp an unrelated ticket. The submit path clears it separately, in
    // `RequestForm` (ASH-915).
    dispatch(setAshSessionId(''))
    closePoptart() // This also resets isMaximized
  }

  // Wrap actions that consumers need to call
  const dispatchPrevHistory = () => dispatch(prevHistory())
  const dispatchSetPoptartHistory = history =>
    dispatch(setPoptartHistory(history))
  const dispatchClearNewMessage = () => dispatch(clearNewMessage())
  const dispatchClearRequestType = () => dispatch(clearRequestType())
  const dispatchClearPopTartHistory = () => dispatch(clearPopTartHistory())

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      isOpen,
      isMaximized,
      openPoptart,
      closePoptart,
      handleClose,
      toggleMaximize,
      requestTitle,
      poptartHistory,
      unresolvedRequests,
      unresolvedRequestsBusy,
      prevHistory: dispatchPrevHistory,
      setPoptartHistory: dispatchSetPoptartHistory,
      clearNewMessage: dispatchClearNewMessage,
      clearRequestType: dispatchClearRequestType,
      clearPopTartHistory: dispatchClearPopTartHistory,
    }),
    [
      isOpen,
      isMaximized,
      requestTitle,
      poptartHistory,
      unresolvedRequests,
      unresolvedRequestsBusy,
    ],
  )

  return (
    <MemberComms.Provider value={contextValue}>{children}</MemberComms.Provider>
  )
}
